import { Link } from 'react-router-dom';
import { Card, Title, Text } from '@tremor/react';
import { Tooltip } from './Tooltip';
import type { DocumentStatsResponse } from '../types';

interface DocumentStatsPanelProps {
  data: DocumentStatsResponse | null;
  selectedBatch?: string | null;
}

/**
 * Calculate ETA based on current throughput
 * Returns formatted string or null if can't calculate
 */
function calculateETA(
  totalDocuments: number,
  completedCount: number,
  firstStartedAt: string | null
): { eta: string; throughput: string } | null {
  if (!firstStartedAt || completedCount === 0 || totalDocuments === 0) {
    return null;
  }

  const remaining = totalDocuments - completedCount;

  if (remaining <= 0) {
    return { eta: 'Complete', throughput: '—' };
  }

  const startTime = new Date(firstStartedAt).getTime();
  const now = Date.now();
  const elapsedMs = now - startTime;

  if (elapsedMs <= 0) {
    return null;
  }

  // Throughput in docs per minute
  const elapsedMinutes = elapsedMs / (1000 * 60);
  const docsPerMinute = completedCount / elapsedMinutes;

  if (docsPerMinute <= 0) {
    return null;
  }

  // ETA in minutes
  const etaMinutes = remaining / docsPerMinute;

  return {
    eta: formatDuration(etaMinutes * 60 * 1000),
    throughput: `${docsPerMinute.toFixed(1)}/min`
  };
}

/**
 * Format milliseconds into human-readable duration
 */
function formatDuration(ms: number): string {
  if (ms < 1000) return '<1s';

  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h`;
  }
  if (hours > 0) {
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  }
  if (minutes > 0) {
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }
  return `${seconds}s`;
}

export function DocumentStatsPanel({ data, selectedBatch }: DocumentStatsPanelProps) {
  if (!data) {
    return (
      <Card className="bg-gray-900 ring-gray-800">
        <Title className="text-gray-100">Document Stats</Title>
        <div className="mt-4 h-64 flex items-center justify-center">
          <Text className="text-gray-500">Loading...</Text>
        </div>
      </Card>
    );
  }

  const { totalDocuments, currentStatuses, currentSteps, evaluation, tagging, runDuration, firstStartedAt, avgSentencesPerDocument, avgPagesPerDocument, totalPagesProcessed, skipped } = data;

  // Calculate ETA - include skipped docs as they're a terminal state
  const completedCount = (currentStatuses.completed || 0) + (currentStatuses.skipped || 0);
  const etaInfo = calculateETA(totalDocuments, completedCount, firstStartedAt);

  // Status colors
  const statusColors: Record<string, string> = {
    Completed: 'text-emerald-400',
    Skipped: 'text-yellow-400',
    Processing: 'text-blue-400',
    Failed: 'text-red-400',
    Pending: 'text-gray-400',
  };

  return (
    <Card className="bg-gray-900 ring-gray-800">
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex items-baseline gap-2 min-w-0">
          <Title className="text-gray-100">Document Stats</Title>
          {selectedBatch && (
            <Text className="text-blue-400 text-sm truncate" title={selectedBatch}>
              — {selectedBatch}
            </Text>
          )}
        </div>
        <Link
          to={`/documents${selectedBatch ? `?batch=${encodeURIComponent(selectedBatch)}` : ''}`}
          className="text-xs text-gray-500 hover:text-gray-300 transition-colors whitespace-nowrap"
        >
          View Documents →
        </Link>
      </div>

      <div className="mt-4 space-y-4">
        {/* Total, Duration, and ETA */}
        <div>
          <div className="flex justify-between items-start gap-4">
            <div>
              <Text className="text-gray-500 text-xs">Total Documents</Text>
              <Text className="text-2xl font-bold text-gray-100">
                {totalDocuments.toLocaleString()}
              </Text>
            </div>
            <div className="flex gap-4 text-right">
              {runDuration && (
                <div>
                  <Text className="text-gray-500 text-xs">Elapsed</Text>
                  <Text className="text-gray-300 font-medium">{runDuration}</Text>
                </div>
              )}
              {etaInfo && (
                <>
                  <div>
                    <div className="flex items-center justify-end">
                      <Text className="text-gray-500 text-xs">Throughput</Text>
                      <Tooltip text="Average documents completed per minute, calculated from total completed documents divided by elapsed time." />
                    </div>
                    <Text className="text-blue-400 font-medium">{etaInfo.throughput}</Text>
                  </div>
                  <div>
                    <div className="flex items-center justify-end">
                      <Text className="text-gray-500 text-xs">ETA</Text>
                      <Tooltip text="Estimated time remaining until all documents complete, based on current throughput: (remaining docs) / (docs per minute)." />
                    </div>
                    <Text className={etaInfo.eta === 'Complete' ? 'text-emerald-400 font-medium' : 'text-amber-400 font-medium'}>
                      {etaInfo.eta}
                    </Text>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Status and Steps - Side by Side */}
        <div className="pt-2 border-t border-gray-800 grid grid-cols-2 gap-4">
          {/* By Status */}
          <div>
            <Text className="text-gray-500 text-xs mb-2">By Status</Text>
            <div className="space-y-1">
              {Object.entries(currentStatuses).map(([status, count]) => {
                const pct = totalDocuments > 0 ? ((count / totalDocuments) * 100).toFixed(1) : '0';
                return (
                  <div key={status} className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${getStatusDotColor(status)}`} />
                    <Text className={`text-sm ${statusColors[capitalizeFirst(status)] || 'text-gray-300'}`}>
                      {count.toLocaleString()}
                    </Text>
                    <Text className="text-gray-500 text-xs">
                      {capitalizeFirst(status)} ({pct}%)
                    </Text>
                  </div>
                );
              })}
            </div>
          </div>

          {/* By Step */}
          <div>
            <Text className="text-gray-500 text-xs mb-2">Currently at Step</Text>
            <div className="space-y-1">
              {Object.entries(currentSteps).map(([step, count]) => (
                <div key={step} className="flex items-center gap-2">
                  <Text className="text-gray-300 text-sm">{count.toLocaleString()}</Text>
                  <Text className="text-gray-500 text-xs">{step}</Text>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Skipped Documents */}
        <div className="pt-2 border-t border-gray-800">
          <div className="flex items-center mb-2">
            <Text className="text-gray-500 text-xs">Skipped Documents</Text>
            <Tooltip text="Documents that were skipped during processing (e.g., unsupported file types, empty documents). These are not included in 'Completed' count." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-800/50 rounded p-2">
              <Text className="text-gray-500 text-xs">Total Skipped</Text>
              <Text className={skipped?.total > 0 ? 'text-yellow-400 font-semibold' : 'text-gray-400 font-semibold'}>
                {(skipped?.total ?? 0).toLocaleString()}
                {totalDocuments > 0 && skipped?.total > 0 && (
                  <span className="text-gray-500 text-xs ml-1">
                    ({((skipped.total / totalDocuments) * 100).toFixed(1)}%)
                  </span>
                )}
              </Text>
            </div>
            <div>
              <Text className="text-gray-500 text-xs mb-1">By Reason</Text>
              {skipped?.byReason && Object.keys(skipped.byReason).length > 0 ? (
                <div className="space-y-1 max-h-24 overflow-y-auto">
                  {Object.entries(skipped.byReason).map(([reason, count]) => (
                    <div key={reason} className="flex items-center gap-2 text-xs">
                      <Text className="text-yellow-400 font-medium min-w-[2rem]">{count}</Text>
                      <Text className="text-gray-400 truncate" title={reason}>
                        {formatSkipReason(reason)}
                      </Text>
                    </div>
                  ))}
                </div>
              ) : (
                <Text className="text-gray-600 text-xs">None</Text>
              )}
            </div>
          </div>
        </div>

        {/* Document Averages */}
        <div className="pt-2 border-t border-gray-800 grid grid-cols-3 gap-4">
          <div className="bg-gray-800/50 rounded p-2">
            <Text className="text-gray-500 text-xs">Avg Sentences/Doc</Text>
            <Text className="text-gray-200 font-semibold">
              {avgSentencesPerDocument != null ? avgSentencesPerDocument.toFixed(1) : '—'}
            </Text>
          </div>
          <div className="bg-gray-800/50 rounded p-2">
            <Text className="text-gray-500 text-xs">Avg Pages/Doc</Text>
            <Text className="text-gray-200 font-semibold">
              {avgPagesPerDocument != null ? avgPagesPerDocument.toFixed(1) : '—'}
            </Text>
          </div>
          <div className="bg-gray-800/50 rounded p-2">
            <Text className="text-gray-500 text-xs">Total Pages</Text>
            <Text className="text-gray-200 font-semibold">
              {(totalPagesProcessed ?? 0).toLocaleString()}
            </Text>
          </div>
        </div>

        {/* Evaluation Results */}
        <div className="pt-2 border-t border-gray-800">
          <Text className="text-gray-500 text-xs mb-2">Evaluation Results</Text>
          <div className={`grid gap-4 ${evaluation.totalClassifierErrors > 0 ? 'grid-cols-4' : 'grid-cols-3'}`}>
            <div className="bg-gray-800/50 rounded p-2">
              <Text className="text-gray-500 text-xs">Evaluated</Text>
              <Text className="text-gray-200 font-semibold">
                {evaluation.documentsEvaluated.toLocaleString()}
              </Text>
            </div>
            <div className="bg-gray-800/50 rounded p-2">
              <Text className="text-gray-500 text-xs">With Labels</Text>
              <Text className={evaluation.documentsWithHarmful > 0 ? 'text-amber-400 font-semibold' : 'text-gray-200 font-semibold'}>
                {evaluation.documentsWithHarmful.toLocaleString()}
                {evaluation.documentsEvaluated > 0 && (
                  <span className="text-gray-500 text-xs ml-1">
                    ({((evaluation.documentsWithHarmful / evaluation.documentsEvaluated) * 100).toFixed(1)}%)
                  </span>
                )}
              </Text>
            </div>
            <div className="bg-gray-800/50 rounded p-2">
              <div className="flex items-center">
                <Text className="text-gray-500 text-xs">Tagged in iConect</Text>
                <Tooltip text="Documents with labels that were uploaded and tagged in iConect. Excludes documents with no labels/folders to assign." />
              </div>
              <Text className="text-emerald-400 font-semibold">
                {(tagging?.successfulUploads ?? 0).toLocaleString()}
                {evaluation.documentsWithHarmful > 0 && (
                  <span className="text-gray-500 text-xs ml-1">
                    ({(((tagging?.successfulUploads ?? 0) / evaluation.documentsWithHarmful) * 100).toFixed(1)}%)
                  </span>
                )}
              </Text>
            </div>
            {evaluation.totalClassifierErrors > 0 && (
              <div className="bg-red-950/30 rounded p-2">
                <Text className="text-gray-500 text-xs">Classifier Errors</Text>
                <Text className="text-red-400 font-semibold">{evaluation.totalClassifierErrors}</Text>
              </div>
            )}
          </div>
        </div>

        {/* Labels, Genres, and Folders - Three columns, stacked below evaluation */}
        <div className="pt-2 border-t border-gray-800 grid grid-cols-3 gap-4">
          {/* Label Counts */}
          <div>
            <div className="flex items-center mb-2">
              <Text className="text-gray-500 text-xs">Labels Found</Text>
              <Tooltip text="Shows total occurrences of each label (by classifier) and how many documents contain it." />
            </div>
            {Object.keys(evaluation.labelCounts).length > 0 ? (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {Object.entries(evaluation.labelCounts)
                  .map(([compositeKey, count]) => {
                    const docCount = evaluation.documentsPerLabel?.[compositeKey] || 0;
                    // Parse composite key (classifierName:label or just label for legacy data)
                    const [classifierName, labelName] = compositeKey.includes(':')
                      ? [compositeKey.split(':')[0], compositeKey.split(':').slice(1).join(':')]
                      : [null, compositeKey];
                    const backend = classifierName
                      ? evaluation.classifierBackends?.[classifierName]
                      : undefined;
                    return (
                      <div key={compositeKey} className="flex items-center gap-2 text-xs">
                        <Text className="text-amber-400 font-medium min-w-[2.5rem]">{count}</Text>
                        <Text className="text-gray-500 min-w-[3.5rem]">({docCount} docs)</Text>
                        <div className="flex items-center gap-1 truncate">
                          {backend && (
                            <span className={`text-[10px] px-1 py-0.5 rounded font-medium shrink-0 ${
                              backend === 'phi'
                                ? 'bg-purple-900/50 text-purple-300'
                                : 'bg-blue-900/50 text-blue-300'
                            }`}>
                              {backend === 'phi' ? 'Phi' : 'OpenAI'}
                            </span>
                          )}
                          <Text className="text-gray-400 truncate">
                            {classifierName ? `${classifierName}:${labelName}` : labelName}
                          </Text>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <Text className="text-gray-600 text-xs">No labels yet</Text>
            )}
          </div>

          {/* Genre Distribution */}
          <div>
            <div className="flex items-center mb-2">
              <Text className="text-gray-500 text-xs">Genres Found</Text>
              <Tooltip text="Document-level genre classification showing how many documents were classified as each genre." />
            </div>
            {evaluation.genreCounts && Object.keys(evaluation.genreCounts).length > 0 ? (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {Object.entries(evaluation.genreCounts).map(([genre, count]) => {
                  const isEdgeCase = ['empty', 'error', 'unknown'].includes(genre);
                  return (
                    <div key={genre} className="flex items-center gap-2 text-xs">
                      <Text className={`font-medium min-w-[2.5rem] ${isEdgeCase ? 'text-gray-500' : 'text-indigo-400'}`}>
                        {count}
                      </Text>
                      <Text className={`truncate ${isEdgeCase ? 'text-gray-600' : 'text-gray-400'}`} title={genre}>
                        {formatGenreDisplay(genre)}
                      </Text>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Text className="text-gray-600 text-xs">No genres yet</Text>
            )}
          </div>

          {/* Folder Assignments */}
          <div>
            <div className="flex items-center mb-2">
              <Text className="text-gray-500 text-xs">Folders Assigned</Text>
              <Tooltip text="Documents assigned to each iConect folder based on their labels." />
            </div>
            {tagging?.folderCounts && Object.keys(tagging.folderCounts).length > 0 ? (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {Object.entries(tagging.folderCounts)
                  .slice(0, 8)
                  .map(([folder, count]) => (
                    <div key={folder} className="flex items-center gap-2 text-xs">
                      <Text className="text-emerald-400 font-medium min-w-[2.5rem]">{count}</Text>
                      <Text className="text-gray-400 truncate" title={folder}>{folder}</Text>
                    </div>
                  ))}
              </div>
            ) : (
              <Text className="text-gray-600 text-xs">No folders yet</Text>
            )}
            {/* Upload stats summary */}
            {tagging && (tagging.successfulUploads > 0 || tagging.failedUploads > 0 || (tagging.uploadsSkippedNoFile ?? 0) > 0) && (
              <div className="mt-2 pt-2 border-t border-gray-800/50 text-xs">
                <div className="flex items-center gap-2 flex-wrap">
                  <Text className="text-gray-500">Uploads:</Text>
                  {tagging.successfulUploads > 0 && (
                    <Text className="text-emerald-400">{tagging.successfulUploads} OK</Text>
                  )}
                  {tagging.failedUploads > 0 && (
                    <Text className="text-red-400">{tagging.failedUploads} failed</Text>
                  )}
                  {(tagging.uploadsSkippedNoFile ?? 0) > 0 && (
                    <Text className="text-yellow-400" title="Documents with labels/folders but no annotated PDF was produced (missing bounding boxes)">{tagging.uploadsSkippedNoFile} no file</Text>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function getStatusDotColor(status: string): string {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'bg-emerald-500';
    case 'skipped':
      return 'bg-yellow-500';
    case 'processing':
      return 'bg-blue-500';
    case 'failed':
      return 'bg-red-500';
    default:
      return 'bg-gray-500';
  }
}

function formatSkipReason(code: string): string {
  // Convert SNAKE_CASE to readable format
  const labels: Record<string, string> = {
    'UNSUPPORTED_MIME_TYPE': 'Unsupported file type',
    'NO_EXTRACTABLE_TEXT': 'No extractable text',
    'EMPTY_DOCUMENT': 'Empty document',
    'PASSWORD_PROTECTED': 'Password protected',
    'CORRUPT_FILE': 'Corrupt file',
  };
  return labels[code] || code.replace(/_/g, ' ').toLowerCase();
}

function formatGenreDisplay(genre: string): string {
  // Edge case labels
  const specialLabels: Record<string, string> = {
    'empty': 'Empty Document',
    'error': 'Classification Error',
    'unknown': 'Unknown',
  };
  if (specialLabels[genre]) return specialLabels[genre];
  // Convert underscore-separated labels to readable format
  // e.g., "Government_Reports_&_Records" → "Government Reports & Records"
  return genre.replace(/_/g, ' ');
}
